/**
 * @file CompanyPlanBadge.tsx
 * @brief Companies list cell — Enterprise shared pool vs team individual seats.
 *
 * @author DES
 * @date 26-07-2026
 */

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { cn } from "@/shared/theme/cn";
import type { AdminCompany } from "../schemas";

function fmtMin(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

export function CompanyPlanBadge({ company }: { company: AdminCompany }) {
  const kind = company.plan_kind || "none";
  const onPlan = kind === "enterprise" || kind === "team";
  const label = company.plan_label || (onPlan ? "Plan" : "Pay as you go");
  const included = Number(company.included_minutes ?? 0);
  const used = Number(company.used_minutes ?? 0);
  const remaining =
    company.remaining_minutes != null
      ? Number(company.remaining_minutes)
      : Math.max(0, included - used);
  const hasPool = onPlan && included > 0;
  const pct = hasPool
    ? Math.min(100, Math.round((used / included) * 100))
    : 0;
  const nearCap = hasPool && pct >= 85;
  const poolMode =
    company.pool_mode === "shared"
      ? "Shared pool"
      : company.pool_mode === "individual"
        ? "Individual pools"
        : null;

  const seatsLimit = company.seats != null ? Number(company.seats) : null;
  const seatsUsed =
    company.seats_used != null ? Number(company.seats_used) : null;
  const seatsLine =
    kind === "enterprise" && seatsLimit != null
      ? `${seatsUsed ?? 0} / ${seatsLimit} seats`
      : kind === "team" && seatsLimit != null
        ? `${seatsLimit} seat${seatsLimit === 1 ? "" : "s"}`
        : null;

  const tip = onPlan
    ? [
        label,
        poolMode,
        seatsLine,
        hasPool
          ? `${fmtMin(used)} used · ${fmtMin(remaining)} left of ${fmtMin(included)}`
          : null,
        company.pool_mode === "individual" && company.minutes_per_seat != null
          ? `${fmtMin(company.minutes_per_seat)} min per seat`
          : null,
        company.plan_status ? `Status: ${company.plan_status}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "No seat plan — corporate pay as you go";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="min-w-[150px] max-w-[220px] space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={cn(
                  "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  kind === "enterprise"
                    ? "bg-brand-soft text-brand"
                    : kind === "team"
                      ? "bg-info-soft text-info"
                      : // Pay as you go: grey on a grey list row disappeared
                        // (DES 01-10-2026: "pills prominent nahin ho rahi");
                        // the page colour with a border and ink stands out.
                        "border border-border bg-background text-foreground",
                )}
              >
                {label}
              </span>
              {poolMode ? (
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {company.pool_mode === "shared" ? "Shared" : "Individual"}
                </span>
              ) : null}
            </div>
            {seatsLine ? (
              <p className="text-[12px] font-medium tabular-nums text-foreground">
                {seatsLine}
              </p>
            ) : null}
            {hasPool ? (
              <>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all",
                      nearCap ? "bg-warning" : "bg-brand",
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-[11px] tabular-nums text-muted-foreground">
                  {fmtMin(used)} / {fmtMin(included)} min
                  {company.pool_mode === "individual" &&
                  company.minutes_per_seat != null
                    ? ` · ${fmtMin(company.minutes_per_seat)}/seat`
                    : ""}
                </p>
              </>
            ) : onPlan ? (
              <p className="text-[11px] text-muted-foreground">No minute pool</p>
            ) : (
              <p className="text-[11px] text-muted-foreground">No seats</p>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
